import { useAsync, useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, constants, utils } from 'ethers/lib/ethers'
import React, { useContext, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import { ArcContext } from 'src/contexts/ArcContext'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import {
  CurveService,
  PoolInfo,
  stableTokenAddresses,
  threePoolAddresses,
} from 'src/services/CurveService'
import { normalizeToWei } from 'src/utils/normalizeToWei'
import useDebounce from 'src/utils/useDebounce'
import { ArcButton } from '../ArcButton'
import { CurveNotice } from '../CurveNotice'
import { Input } from '../Input'
import './Deposit.scss'
import { DepositInfo } from './DepositInfo'

const threePoolTokens = Object.keys(threePoolAddresses)
const threePoolInputAssets = threePoolTokens.map((erc20Name) => ({
  label: erc20Name,
  value: erc20Name,
}))
const stableTokens = Object.keys(stableTokenAddresses)
const stableInputAssets = stableTokens.map((erc20Name) => ({
  label: erc20Name,
  value: erc20Name,
}))

interface IProps {
  curveService: CurveService
  poolInfo: PoolInfo
}

export function Deposit({ curveService, poolInfo }: IProps) {
  const { isLoading, setIsLoading } = useContext(ArcContext)

  const [threePoolToken, setThreePoolToken] = useState<string>(threePoolTokens[0])
  const threePoolTokenBalance = useErc20Balance(
    threePoolAddresses[threePoolToken],
    curveService.signer,
  )
  const threePoolTokenDetails = useErc20Details(
    threePoolAddresses[threePoolToken],
    curveService.signer,
  )
  const [threePoolTokenDepositAmount, setThreePoolTokenDepositAmount] =
    useState(threePoolTokenBalance)
  const debounced3poolDepositAmount = useDebounce(threePoolTokenDepositAmount, 1000)

  const [stableToken] = useState<string>(stableTokens[0])
  const stableTokenBalance = useErc20Balance(stableTokenAddresses[stableToken], curveService.signer)
  const stableTokenDetails = useErc20Details(stableTokenAddresses[stableToken], curveService.signer)
  const [stableTokenDepositAmount, setStableTokenDepositAmount] = useState(stableTokenBalance)
  const debouncedStableDepositAmount = useDebounce(stableTokenDepositAmount, 1000)

  const [expectedAmount] = useAsync<BigNumber>(
    () =>
      curveService.calcExpectedDepositAmount([
        {
          erc20Address: threePoolAddresses[threePoolToken],
          amount: debounced3poolDepositAmount,
        },
        {
          erc20Address: stableTokenAddresses[stableToken],
          amount: debouncedStableDepositAmount,
        },
      ]),
    [debounced3poolDepositAmount, debouncedStableDepositAmount],
  )
  const stableDepositedPercentage = threePoolTokenDetails
    ? calcTargetStablePercentage(
      poolInfo,
      debouncedStableDepositAmount,
      normalizeToWei(debounced3poolDepositAmount, threePoolTokenDetails.decimals),
    )
    : constants.Zero

  const isActionDisabled =
    !expectedAmount ||
    !threePoolTokenDetails ||
    !stableTokenDetails ||
    debounced3poolDepositAmount.gt(threePoolTokenBalance) ||
    !threePoolTokenDepositAmount.eq(debounced3poolDepositAmount) ||
    debouncedStableDepositAmount.gt(stableTokenBalance) ||
    !stableTokenDepositAmount.eq(debouncedStableDepositAmount) ||
    !curveService.isPoolStable(stableDepositedPercentage) ||
    debounced3poolDepositAmount.isZero()

  return (
    <div className="deposit">
      <Input
        balance={stableTokenBalance}
        decimals={stableTokenDetails?.decimals}
        label="Deposit 1"
        assets={stableInputAssets}
        setAmount={setStableTokenDepositAmount}
        selectedAsset={stableToken}
        maxAmount={stableTokenBalance}
        loading={!stableTokenDetails}
      />
      <Input
        balance={threePoolTokenBalance}
        decimals={threePoolTokenDetails?.decimals}
        label="Deposit 2"
        assets={threePoolInputAssets}
        onAssetChanged={setThreePoolToken}
        setAmount={setThreePoolTokenDepositAmount}
        selectedAsset={threePoolToken}
        maxAmount={threePoolTokenBalance}
        loading={!threePoolTokenDetails}
      />
      <p className="deposit__description">
        {`You will receive ${
          expectedAmount ? getReadableNumber(expectedAmount) : '...'
        } STABLEx3CRV (LP tokens)`}
      </p>
      <DepositInfo curveService={curveService} />
      <ReactTooltip disable={!isActionDisabled} id="deposit__disabled-arc-button" />
      <span
        data-for="deposit__disabled-arc-button"
        data-place="top"
        data-tip={`Deposited collateral amount has to be more than 0. The pool cannot be imbalanced by more than ${curveService.config.minimalPoolPartPercentage}%`}
        data-effect="solid"
        className="deposit__tooltip"
      >
        <ArcButton
          onClick={async () => {
            try {
              setIsLoading(true)
              await curveService.deposit(
                [
                  {
                    erc20Address: threePoolAddresses[threePoolToken],
                    amount: debounced3poolDepositAmount,
                  },
                  {
                    erc20Address: stableTokenAddresses[stableToken],
                    amount: debouncedStableDepositAmount,
                  },
                ],
                expectedAmount,
              )
            } finally {
              setIsLoading(false)
            }
          }}
          name="Deposit"
          disabled={isActionDisabled}
          loading={isLoading}
        />
      </span>
      <CurveNotice />
    </div>
  )
}

function calcTargetStablePercentage(
  poolInfo: PoolInfo,
  normalizedStableTokenValue: BigNumber,
  normalized3PoolValue: BigNumber,
) {
  const targetStablePoolDeposited = poolInfo.tokenDeposited.add(normalizedStableTokenValue)
  const targetThreePoolDeposited = poolInfo.threePoolDeposited.add(normalized3PoolValue)
  return utils
    .parseEther('100')
    .mul(targetStablePoolDeposited)
    .div(targetStablePoolDeposited.add(targetThreePoolDeposited))
}
